import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo
} from 'react';
import { Observable, Subject } from 'rxjs';
import { WidgetPayload } from './widget.types';

type WidgetContextValue = {
  openWidget$: () => Observable<WidgetPayload>;
  openWidget: (payload: WidgetPayload) => void;
};

const WidgetContext = createContext<WidgetContextValue | undefined>(undefined);

export const WidgetProvider = ({ children }: { children: React.ReactNode }) => {
  const subject = useMemo(() => new Subject<WidgetPayload>(), []);

  const openWidget$ = useCallback((): Observable<WidgetPayload> => {
    return subject.asObservable();
  }, [subject]);

  const openWidget = useCallback(
    (payload: WidgetPayload) => {
      subject.next(payload);
    },
    [subject]
  );

  useEffect(() => {
    return () => {
      subject.complete();
    };
  }, [subject]);

  const value = useMemo<WidgetContextValue>(
    () => ({
      openWidget$,
      openWidget
    }),
    [openWidget$, openWidget]
  );

  return React.createElement(WidgetContext.Provider, { value }, children);
};

export const useWidget = () => {
  const context = useContext(WidgetContext);
  if (!context) {
    throw new Error('useWidget must be used within WidgetProvider');
  }
  return context;
};
